import type { AppContext, AppProps } from "next/app";
import Script from "next/script";
import { useRouter } from "next/router";
import { useEffect } from "react";
import sdk from "../sdk";
import type { AgencyWebsiteProfile } from "@serviestate/shared";
import Footer from "../components/footer";
import Header from "../components/header";
import Providers from "../providers";
import { getAgencyIdFromRequest } from "../utils";
import "./globals.css";
import { Box, styled } from "@mui/material";
import { appWithTranslation } from "next-i18next";
import PageLoading from "../components/page-loading";
import { BannerWebsiteDemo } from "../components/banner-website-demo";
import Head from "next/head";
import FloatingWhatsAppButton from "../components/floating-whatsapp-button";

function App({
  Component,
  pageProps,
  agency,
}: AppProps & { agency: AgencyWebsiteProfile }) {
  const router = useRouter();
  const gtmId = process.env.NEXT_PUBLIC_GTM_ID;

  useEffect(() => {
    if (!gtmId) return;

    const onRouteChange = (url: string) => {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: "page_view",
        page_path: url,
      });
    };

    router.events.on("routeChangeComplete", onRouteChange);
    return () => router.events.off("routeChangeComplete", onRouteChange);
  }, [router.events, gtmId]);

  return (
    <>
      {gtmId && (
        <>
          <Script
            id="gtm-init"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${gtmId}');
              `,
            }}
          />
          <Script
            id="gtm-datalayer"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
              `,
            }}
          />
        </>
      )}
      <Providers agency={agency}>
        <Head>
          <link rel="icon" type="image/png" href={"/favicon.ico"} />
        </Head>
        <PageLoading />
        <Box
          sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
        >
          <Header />
          <MainContent>
            <Component {...pageProps} />
          </MainContent>
          <Footer />
          {agency?.demo_mode && <BannerWebsiteDemo />}
        </Box>
        <FloatingWhatsAppButton
          phoneNumber={agency?.phone_mobile ?? agency.mobile}
          defaultMessage={
            agency?.name
              ? `Hola ${agency.name}, me gustaria obtener mas informacion.`
              : undefined
          }
        />
      </Providers>
    </>
  );
}

const MainContent = styled("main")({
  flex: 1,
  display: "flex",
  flexDirection: "column",
});

async function getAgencyInfo(agencyId: string) {
  if (!agencyId) return null;
  const profile = await sdk.website.getProfile({ agency_id: agencyId });
  if (!profile) return null;
  return profile;
}

App.getInitialProps = async (app: AppContext) => {
  const agencyId = getAgencyIdFromRequest(app.ctx.req);
  const agency = await getAgencyInfo(agencyId);
  return {
    agency,
  };
};

export default appWithTranslation(App);
